import { MenuItem, Modal, Select } from '@material-ui/core';
import React, { ReactElement } from 'react';

import { IBidderGroup } from '~/src/dash/api/bidder-group';
import { IBidderGroupLabel } from '~/src/dash/api/bidder-group-label';
import { IChannelGroupLabel } from '~/src/dash/api/channel-group-label';
import { IChannelGroup } from '~/src/dash/api/console-api';
import { highlightedDivStyle } from '~/src/dash/components/layout/common';
import { MaterialLabelDropdownMulti } from '~/src/dash/components/material-label-dropdown-multi';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { ButtonV2, Col, Cols, Row, Rows, spacing, Text, TextInput } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { ISessionStateProps, IUserStateProps, IWithStylesProps } from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import { siteWeightOptions } from '~/src/dash/utils/site-weights';

const labelWidth = 108;
const rowHeight = 40;

export interface IProps extends IWithStylesProps, ISessionStateProps, IUserStateProps {
  active: boolean;
  groupInfo?: IBidderGroup | IChannelGroup;
  list: IMenuList;
  menuTitle: string;
  showPrefixID: boolean;

  // quick filter related prop
  quickFilterOptions?: Array<{ title: string; optionItems: IMenuList; type: string }>;

  // Bidder / Channel Group Labels
  loadingGroup: boolean;
  allGroupLabels: Array<IBidderGroupLabel | IChannelGroupLabel>;
  selectedGroupLabels: Array<IBidderGroupLabel | IChannelGroupLabel>;
  handleChangeSelectedGroupLabels: (
    groupLabels: Array<IBidderGroupLabel | IChannelGroupLabel>,
  ) => void;

  onClose?: () => void;
  onUpdate?: (name: string, ids: string, defaultWeight?: number) => void;
  onSave?: () => void;
  handleItemAcceptClick: (list: IMenuList) => string;
}

interface IState {
  Name: string;
  IDsValue: string;
  DefaultWeight?: number;
  list: IMenuList;
  [key: string]: any;
  helperText: string;
}

const getInitalIDValues = (groupInfo?: IBidderGroup | IChannelGroup): string => {
  if (groupInfo && 'BidderIDs' in groupInfo) {
    return groupInfo.BidderIDs;
  } else if (groupInfo && 'ChannelIDs' in groupInfo) {
    return groupInfo.ChannelIDs;
  }
  return '';
};

const getDefaultWeight = (groupInfo?: IBidderGroup | IChannelGroup): number | undefined => {
  if (groupInfo && 'ChannelIDs' in groupInfo) {
    if (groupInfo.DefaultWeight) {
      return groupInfo.DefaultWeight;
    }
    // If new channel group, set weight to default value of 1
    return 1;
  }
  return undefined;
};

export const GroupSettingsDialog = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      section: {
        display: 'block',
        marginBottom: theme.spacing.unit * 4,
      },
      dialogPaper: {
        minHeight: '80vh',
        maxHeight: '80vh',
      },
    }),
  },
  class extends React.Component<IProps, IState> {
    state = {
      Name: this.props.groupInfo ? this.props.groupInfo.Name : '',
      IDsValue: getInitalIDValues(this.props.groupInfo),
      DefaultWeight: getDefaultWeight(this.props.groupInfo),
      list: this.props.list,
      helperText: '',
    };

    componentWillReceiveProps(nextProps: IProps) {
      if (this.props.groupInfo && nextProps.groupInfo) {
        // if the current and next group info ID is the same, use the state's Name
        if (this.props.groupInfo.ID === nextProps.groupInfo.ID) {
          this.setState({
            Name: this.state.Name,
            IDsValue: this.state.IDsValue,
            DefaultWeight: getDefaultWeight(nextProps.groupInfo),
            list: nextProps.active !== this.props.active ? nextProps.list : this.state.list,
          });
          return;
        }
      }
      this.setState({
        Name: nextProps.groupInfo ? nextProps.groupInfo.Name : '',
        IDsValue: getInitalIDValues(nextProps.groupInfo),
        DefaultWeight: getDefaultWeight(nextProps.groupInfo),
        list: nextProps.list,
      });
    }

    handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState(
        {
          [target.name]: target.value,
        },
        () => {
          if (this.state.helperText !== '' && this.state.Name !== '') {
            this.setState({
              helperText: '',
            });
          }
        },
      );
    };

    onClose = () => {
      if (this.props.onClose) {
        this.props.onClose();
      }
    };

    onUpdate = () => {
      const { DefaultWeight, IDsValue, Name } = this.state;
      if (Name === '') {
        this.setState({
          helperText: 'Please enter a group name before saving.',
        });
      } else {
        if (this.props.menuTitle && this.props.onUpdate) {
          if (this.props.menuTitle === 'Channel') {
            this.props.onUpdate(Name, IDsValue, DefaultWeight);
          } else if (this.props.menuTitle === 'Bidder' || this.props.menuTitle === 'Bidders') {
            this.props.onUpdate(Name, IDsValue);
          }
        }
        if (this.props.onSave) {
          this.props.onSave();
        }
      }
    };

    handleItemAcceptClick = (): void => {
      this.setState({
        IDsValue: this.props.handleItemAcceptClick(this.state.list),
      });
    };

    handleChangeDefaultWeight = (value: string) => {
      const weightNumber = parseInt(value, 10);
      this.setState({
        DefaultWeight: weightNumber,
      });
    };

    // renderGroupLabels renders the group labels used by both the bidder settings list
    // view & the channel settings list view
    renderGroupLabels = (): ReactElement | null => {
      const {
        allGroupLabels,
        handleChangeSelectedGroupLabels,
        loadingGroup,
        menuTitle,
        selectedGroupLabels,
      } = this.props;

      return (
        <MaterialLabelDropdownMulti
          loading={loadingGroup}
          menuTitle={`${menuTitle} Group Labels`}
          list={allGroupLabels || ([] as Array<IBidderGroupLabel | IChannelGroupLabel>)}
          selected={selectedGroupLabels || ([] as Array<IBidderGroupLabel | IChannelGroupLabel>)}
          onChange={(groupLabels: Array<IBidderGroupLabel | IChannelGroupLabel>) => {
            handleChangeSelectedGroupLabels(groupLabels);
          }}
          width='280px'
        />
      );
    };

    render() {
      const { active, menuTitle, quickFilterOptions } = this.props;

      const { list, DefaultWeight, Name } = this.state;

      const groupInfo = this.props.groupInfo || ({ ID: '' } as IBidderGroup | IChannelGroup);

      return (
        <Modal
          open={active}
          onClose={this.onClose}
          aria-labelledby='create-new-channel-list-modal'
          aria-describedby='create-new-channel-list-modal'
        >
          <Rows
            smallVerticalPadding
            extraLargeHorizontalPadding
            style={{
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '720px',
              height: '460px',
              ...highlightedDivStyle,
            }}
          >
            <Row noHorizontalPadding verticalPadding>
              <Text
                extraLarge
                bold
                style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {groupInfo && String(groupInfo.ID) === '0'
                  ? 'Creating New Group'
                  : `Editing Group - [${groupInfo.ID}] ${Name}`}
              </Text>
              <Text
                tKey={
                  menuTitle === 'Channel'
                    ? 'ChannelsTextInputNameSelectChannelsAddLabels'
                    : 'BiddersTextInputNameSelectBiddersAddLabels'
                }
              />
            </Row>
            {/* FORMS */}
            <Row noPadding>
              <Rows gap={spacing.small} style={{ alignItems: 'center', justifyContent: 'center' }}>
                <Row noPadding height={rowHeight} style={{ width: '100%' }}>
                  <Cols gap={spacing.extraLarge} style={{ alignItems: 'end' }}>
                    <Col width={labelWidth}>
                      <Text small bold>
                        Name
                      </Text>
                    </Col>
                    <Col flex>
                      <TextInput
                        width={'280px'}
                        enableClear
                        initialValue={Name}
                        handleOnChange={(value: string) =>
                          this.setState({
                            Name: value,
                          })
                        }
                        inputSize='small'
                        isAutoFocused
                        msDelay={500}
                        placeholder={`Name`}
                        placeholderTKey='Name'
                      />
                    </Col>
                  </Cols>
                </Row>
                <Row paddingTop='15px' noPadding height={rowHeight} style={{ width: '100%' }}>
                  <Cols gap={spacing.extraLarge} style={{ alignItems: 'end' }}>
                    <Col width={labelWidth}>
                      <Text small bold>
                        {menuTitle === 'Channel' ? `Channels` : `Bidders`}
                      </Text>
                    </Col>
                    <Col flex>
                      <MaterialSearchDropdownMulti
                        getValueFromSearchElement={(src: IListItem) => src.title}
                        selectAll={true}
                        menuTitle={menuTitle}
                        list={list}
                        onChange={(list: IMenuList) => this.setState({ list })}
                        handleMenuAcceptClick={this.handleItemAcceptClick}
                        quickFilterOptions={quickFilterOptions}
                        showPrefixID={!!this.props.showPrefixID}
                        width='280px'
                      />
                    </Col>
                  </Cols>
                </Row>
                <Row noPadding height={rowHeight} style={{ width: '100%' }}>
                  <Cols gap={spacing.extraLarge} style={{ alignItems: 'end' }}>
                    <Col width={labelWidth}>
                      <Text small bold>
                        {menuTitle === 'Channel' ? `Channel Labels` : `Bidder Labels`}
                      </Text>
                    </Col>
                    <Col flex>
                      {/* Bidder / Channel Group Labels */}
                      {this.renderGroupLabels()}
                    </Col>
                  </Cols>
                </Row>
                <Row noPadding height={rowHeight} style={{ width: '100%' }}>
                  {this.props.session.publisher.ID === '788' && 'DefaultWeight' in groupInfo && (
                    <Cols gap={spacing.extraLarge} style={{ alignItems: 'end' }}>
                      <Col width={labelWidth}>
                        <Text small bold>
                          Default Channel Group Weight
                        </Text>
                      </Col>
                      <Col flex>
                        <Select
                          labelId='default-cg-weight-label'
                          name='DefaultWeight'
                          style={{ marginRight: 20 }}
                          value={DefaultWeight}
                          onChange={(event: React.ChangeEvent<{ _: string; value: string }>) => {
                            this.handleChangeDefaultWeight(event.target.value);
                          }}
                        >
                          {siteWeightOptions.map((w) => {
                            return <MenuItem value={w.value}>{w.label}</MenuItem>;
                          })}
                        </Select>
                      </Col>
                    </Cols>
                  )}
                </Row>
              </Rows>
            </Row>
            <Row style={{ height: '100px' }}></Row>
            <Row noHorizontalPadding>
              <Cols
                gap={spacing.large}
                style={{
                  justifyContent: 'flex-end',
                  alignItems: 'end',
                  width: '100%',
                }}
              >
                <Col>
                  <ButtonV2 small onClick={this.onUpdate}>
                    Save
                  </ButtonV2>
                </Col>
                <Col>
                  <ButtonV2 small onClick={this.onClose} clearBackground color={publicaColors.blue}>
                    Cancel
                  </ButtonV2>
                </Col>
              </Cols>
            </Row>
          </Rows>
        </Modal>
      );
    }
  },
);
